import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, merge, switchMap, take } from 'rxjs';
import {
  selectCompanyInformationData,
  selectCompanyInformationError,
  selectCompanyInformationLoading,
} from '@modules/company-profile/store/company-profile.selectors';
import { companyInformationRequest } from '@modules/company-profile/store/company-profile.action';
import { CompanyData } from '@modules/company-profile/responses/company-profile.response';
import { RegistrationStatus } from '@modules/companies/types/company';
import { ErrorCodes } from '@shared/enums/custom-error-code';
import { CompanyInfoInclusionRequest } from '@modules/company-profile/request/company-info-inclusion.request';

const ALLOWED_STATUSES: RegistrationStatus[] = [
  RegistrationStatus.APPLICATION_SUBMITTED,
  RegistrationStatus.SUBMITTED,
  RegistrationStatus.PENDING_APPROVAL,
  RegistrationStatus.APPROVED,
  RegistrationStatus.REGISTERED,
  RegistrationStatus.COMPLETED,
];

export const companyRegistrationGuard: CanActivateFn = () => {
  const store = inject(Store);
  const router = inject(Router);

  const inclusiveObject: CompanyInfoInclusionRequest = {
    attachmentsIncluded: false,
    brelaVerificationRequest: false,
    contactDetailsIncluded: false,
    includeOMC: false,
    includeRegistrationProgress: false,
    includeStakeholders: false,
    includeSupplier: false,
    includeTerminal: false,
    includeNonBpsOperator: false,
  };

  const resolveFromResponse$ = store.select(selectCompanyInformationData).pipe(
    filter((response) => !!response?.data),
    take(1),
    map((response) => {
      const data = (response?.data?.getMyCompany?.data ??
        response?.getMyCompany?.data ??
        response?.data?.data ??
        response?.data) as CompanyData;

      if (ALLOWED_STATUSES.includes(data?.registrationStatus as RegistrationStatus)) {
        return true;
      }

      return router.createUrlTree(['/company-profile/company-type-selection']);
    }),
  );

  const resolveFromError$ = store.select(selectCompanyInformationError).pipe(
    filter((error) => !!error?.data),
    take(1),
    map((error) => {
      if (error.data?.code === ErrorCodes.NO_RECORD_FOUND) {
        return router.createUrlTree(['/company-profile/company-type-selection']);
      }

      return router.createUrlTree(['/company-profile/company-type-selection']);
    }),
  );

  return combineLatest([
    store.select(selectCompanyInformationData).pipe(take(1)),
    store.select(selectCompanyInformationError).pipe(take(1)),
    store.select(selectCompanyInformationLoading).pipe(take(1)),
  ]).pipe(
    switchMap(([response, error, loading]) => {
      const hasResponse = Boolean(response?.data);
      const hasError = Boolean(error?.data);

      if (hasResponse) {
        return resolveFromResponse$;
      }

      if (!loading && !hasError) {
        store.dispatch(
          companyInformationRequest({
            request: {
              uuid: '',
              ...inclusiveObject,
            },
          }),
        );
      }

      return merge(resolveFromResponse$, resolveFromError$).pipe(take(1));
    }),
    take(1),
  );
};
